import React, { useState } from 'react';
import getCsrfToken from '@99designs/frontend-components/utils/app/getCsrfToken';

import { Source } from '../../common/constants';
import { PageType } from '../../common/types';

import { useAuthConfigContext } from '../AuthConfigContext';
import { Login, LoginProps } from '../Login';
import { Masquerading } from '../Masquerading';
import { SignUp, SignUpProps } from '../SignUp';
import { TermsUpdate } from '../TermsUpdate';
import { VerifyLogin } from '../VerifyTwoFactor/VerifyLogin';

export interface ContainerProps {
    clientError?: string;
    email?: string;
    page: PageType;
    recaptchaSkipToken?: string;
    resetSuccessReason?: string;
    setPage: (page: PageType, link: string) => void;
    showRecaptcha?: boolean;
    source: Source;
    target?: string;
    variant?: 'client' | 'designer' | 'invite';
    onAuthSuccess: (target: string) => void;
}

export function Container({
    clientError,
    email,
    page,
    recaptchaSkipToken,
    resetSuccessReason,
    setPage,
    showRecaptcha = true,
    source,
    target,
    variant,
    onAuthSuccess,
}: ContainerProps) {
    const { microfrontend = false } = useAuthConfigContext();
    const [postLoginUrl, setPostLoginUrl] = useState('');

    function handleSubmit(endpoint: 'login' | 'signup', data: Record<string, unknown>) {
        const query = new URLSearchParams({
            ...(target && { target }),
            ...(recaptchaSkipToken && { 'recaptcha-skip-token': recaptchaSkipToken }),
        });

        const url = new URL(`/accounts/xhr/${endpoint}`, window.location.origin);
        url.search = query.toString();

        return fetch(url.toString(), {
            method: 'POST',
            headers: { Accept: 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({
                microfrontend,
                source,
                csrf_token: getCsrfToken(),
                ...data,
            }),
        })
            .then((response) => response.json().then((d) => ({ status: response.status, body: d })))
            .then((d) => {
                if (d.status === 200) {
                    if (d.body.must_verify_2fa) {
                        setPage('verifyLogin', d.body.target);
                        return d;
                    }

                    if (d.body.must_view_terms) {
                        setPostLoginUrl(d.body.target);
                        setPage('termsUpdate', '');
                        return d;
                    }

                    if (d.body.target) {
                        onAuthSuccess(d.body.target);
                    }

                    return d;
                }

                return d;
            });
    }

    const props = {
        onSubmit: handleSubmit,
        setPage,
        showRecaptcha,
        source,
        target,
    };

    if (page === 'login') {
        const loginProps: LoginProps = {
            ...props,
            clientError,
            resetSuccessReason,
        };

        return <Login {...loginProps} />;
    } else if (page === 'signup') {
        const signUpProps: SignUpProps = {
            ...props,
            email,
        };

        if (variant === 'invite') {
            return <SignUp hideRoleSelector userRole="designer" {...signUpProps} />;
        } else if (variant === 'designer') {
            return <SignUp userRole="designer" {...signUpProps} />;
        } else if (variant === 'client') {
            return <SignUp hideRoleSelector userRole="client" {...signUpProps} />;
        }

        return <SignUp {...signUpProps} />;
    } else if (page === 'verifyLogin') {
        return <VerifyLogin onAuthSuccess={onAuthSuccess} />;
    } else if (page === 'masquerading') {
        return <Masquerading />;
    } else if (page === 'termsUpdate') {
        return <TermsUpdate redirectUrl={postLoginUrl} onAuthSuccess={onAuthSuccess} />;
    }

    return null;
}
